/**
 * 🔴 **This side-effect import MUST STAY FIRST — it is what makes the `export … from
 * "@rista/core/helpers/commonHelpers"` lines further down survive a circular import.**
 *
 * A re-export compiles to a getter that reads the imported module's variable, and that variable is
 * assigned where its import sits in the hoisted import list — it does NOT hoist the way the
 * `export function` declarations these lines replaced did. This file sits in several app cycles
 * (e.g. `commonHelpers` → … → `featureRestrictionHelper` → `LeftMenu/menuItemsData`, which calls
 * `isMobileView()` at module-EVAL time), so a re-entry while the app imports below are still
 * running would read the getter before the core variable exists →
 * `TypeError: Cannot read properties of undefined (reading 'isMobileView')`.
 *
 * Importing the core module FIRST assigns that variable before any app import can cycle back.
 * `@rista/core` never imports from `apps/*`, so nothing it pulls in can re-enter this file.
 */
import "@rista/core/helpers/commonHelpers";

import moment from "moment";
import { updateInfoModal, updateToastInfo } from "redux-store/slices/CheckoutSlice";
import { saveSaleData } from "redux-store/slices/SaleSlice";
import { getLocalStorage, setLocalStorage, removeLocalStorage, clearAppLsData } from "utils";
import { getErrorMessage } from "utils/pos_errors";
import store from '../redux-store/index';
import { isKioskModeEnabled } from "@rista/core/kiosk/config";
import { markSessionExpired } from "@rista/core/redux-store/slices/kioskSlice";
import { getChannelDefData } from "./catalogueHelpers";
import { getClonedSaleDataFromReducer, getSelectedChannel } from "./saleHelpers";
import { PROCESSED, LOCALSTORAGE_KEYS, NVEG, EGG, VEG, CASH_REGISTER_MODAL_TYPES, REQUIRED } from "utils/constants";
import { ITEM_TYPE, ITEM_TYPE_CONFIG, ItemType } from "data/constants";
import { LOGIN_ROUTE } from "utils/routeConstants";
import history from "../history";
import { updateNotificationValue, updateOfflineLogin } from "redux-store/slices/GlobalSlice";
import { exec, isAndroid, sendDataToApp } from "utils/nativeHooks";
import { stopDesktopNotification } from "./myOrderHelpers";
import { resetOfflineData, setOffline } from "./OfflineHelper";
import { supportLogout, userLogout } from "redux-store/slices/LoginSlice";
import { APP_SOURCE, APP_SOURCE_TYPES, CLARITY_ACCOUNT_UUIDS, LOGIN_ATTEMPT_INITIAL_COUNT, NOTIFICATIONS_EVENTTYPES, SELECTION_MODES, NW_APP_EVENT_KEYS, REACT_NATIVE_APP_EVENTS, viewTypes } from "data/constants";
import { getPOSSettingsData } from "redux-store/slices/catalogueSlice";
import { isOffline } from "facade/ServiceFacade";

import { SaleDataType } from "models/sale";

import checkIsEmpty from "lodash/isEmpty";
import { setModuleSettingsLoaded } from "redux-store/slices/PosSettingsSlice";
import { LOG_LEVELS, logEvent } from 'helper/cartAuditHelper';

declare global {
  interface Window {
    RL?: any;
  }
}
// Moved to @rista/core/helpers/commonHelpers (both platforms — this side measures the window,
// the other returns a constant true).
export { isMobileView } from "@rista/core/helpers/commonHelpers";


export function isSupportLogin() {
  let isSupport = getLocalStorage(LOCALSTORAGE_KEYS.isSupportLogin);
  return isSupport ? JSON.parse(isSupport) : false;
}

// DP-8122's blocked-customer check lives in @rista/core/helpers/commonHelpers (shared.ts — the
// two apps' copies were identical) as `useIsCustomerBlocked`: it calls `useSelector`, so the
// definition carries the hook prefix. Re-exported under DP-8122's name so its call sites are
// unchanged; nothing in this file's own body calls it (ledger rule 5).
export { useIsCustomerBlocked as isCustomerBlocked } from "@rista/core/helpers/commonHelpers";

// export function deviceDetector(){
//   let hasTouchScreen = false;
//     if ("maxTouchPoints" in navigator) {
//       hasTouchScreen = navigator.maxTouchPoints > 0;
//     } else if ("msMaxTouchPoints" in navigator) {
//       hasTouchScreen = navigator.maxTouchPoints > 0;
//     } else {
//       const mQ = matchMedia("(pointer:coarse)");
//       if (mQ && mQ.media === "(pointer:coarse)") {
//         hasTouchScreen = !!mQ.matches;
//       } else if ("orientation" in window) {
//         hasTouchScreen = true; // deprecated, but good fallback
//       } else {
//         // Only as a last resort, fall back to user agent sniffing
//         var UA = navigator.userAgent;
//         hasTouchScreen =
//           /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
//           /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA);
//       }
//     }
//     if (hasTouchScreen) {
//       return "mobile";
//     } else {
//       return "desktop";
//     }
// }

export function numbersOnly(value: any = 0) {
  let regex = /^\d+$/, isValid = false;
  isValid = regex.test(value) ? true : false;
  return isValid;
}

// `deepClone` now lives in @rista/core — one definition, no drift. Re-export only:
// nothing in this file calls it.
export { deepClone } from "@rista/core/helpers/commonHelpers";

/**
 * ⚠️ **ARITHMETIC — never display-config-driven.** This decides what is CHARGED and SENT, so it
 * stays on two decimals whatever a merchant's DISPLAY precision is set to. What a screen shows
 * goes through `@rista/core/utils/currency`'s `amount()` / `money()` instead; wire that in here
 * and the day a merchant is configured for three, the amount charged stops matching the quote.
 */
export function roundOffTwoDecimal(value: number) {
  return (Math.round(value * 100) / 100).toFixed(2)
}

/** Numeric twin of roundOffTwoDecimal — use for money math, not display. */
export function round2(value: number) {
  return parseFloat(roundOffTwoDecimal(value));
}

export function getNext10DaysList() {
  let currentDate = moment().format(),
    arr = Array.from({ length: 10 }, (_, i) => i + 1), // creation of array from 1 to 10
    result: any = [];

  arr.map((val) => {
    result.push({
      dateVal: moment(currentDate).add(val, 'day').format("DD"),
      monthVal: moment(currentDate).add(val, 'day').format("MMM"),
      dayVal: moment(currentDate).add(val, 'day').format("ddd"),
      momentVal: moment(currentDate).add(val, 'day').format()
    })
  });
  result.unshift({
    dateVal: moment(currentDate).format("DD"),
    monthVal: moment(currentDate).format("MMM"),
    dayVal: moment(currentDate).format("ddd"),
    momentVal: moment(currentDate).format()
  })
  return result;
}
export function buildBrandList(channelDef: any[], bizBrands: any[], loginBranchId: string) {
  const unique: string[] = [];
  const brandList: { brandId: string; brandName: string }[] = [];

  const addBrand = (brandId: string) => {
    if (brandId && unique.indexOf(brandId) === -1) {
      const brandRec = bizBrands.find((b: any) => b.mid === brandId) || null;
      brandList.push({ brandId, brandName: brandRec ? brandRec.brandName : "" });
      unique.push(brandId);
    }
  };

  if (channelDef && channelDef.length) {
    channelDef.forEach((item: any) => {
      const data = item || {};

      if (data.variants && data.variants.length > 0) {
        data.variants.forEach((rec: any) => {
          if (rec.branchIds && rec.branchIds.includes(loginBranchId) && rec.labelRequired) {
            addBrand(data.brandId);
          }
        });
      } else if (data.labelRequired) {
        addBrand(data.brandId);
      }
    });
  }

  return brandList;
}

// Moved to @rista/core/helpers/commonHelpers so the shared customer-summary panel prints the
// same mask this app does. NOTE this is NOT `helper/customerHelpers`' `maskPhoneNumber`, which
// is a different masking scheme — see the core docstring.
export { maskPhoneNumber } from "@rista/core/helpers/commonHelpers";

export const customerName: any = () => {
  const customerInfo = store.getState().customerStore?.customerInfo;
  console.log("customerInfo ", customerInfo)
  let firstName = customerInfo?.contactInfo?.name?.firstName || '', name = '', lastName = customerInfo?.contactInfo?.name?.lastName || '';
  name += firstName + (lastName ? ` ${lastName}` : '');
  return name;
}

export const handleWarningModal: any = (error: any) => {
  let data = {
    isOpen: true,
    message: getErrorMessage(error)
  };
  store.dispatch(updateInfoModal(data));
}

export const checkoutValidationError: any = (checkForCustInfo = true, selectedMode?: string) => {
  /* Validations
--- In sale channel setting -------------------------------------------------------------------------------------------------------------------
     autoGenerateLabel is checked   and serviceLabel is required
 
     then
 
----  if the adhoc label is empty and autoGenerateLabel ==true  then it will generate new token and set to service label
      else user has to enter audhoc label and that is set to service label
*/
  const selectedChannelData = getSelectedChannel(), saleData = store.getState().saleDataStore?.saleData, channelDefVariant = getChannelDefData(saleData.channelDefId),
    businessData = store.getState().businessStore?.businessData,
    channelDefCatalogData = store.getState().catalogStore.channelDef || {};

  let windowObj: any = window;
  let data = windowObj.RL.saleutil, isValid = true;

  let newSaleData = structuredClone(saleData);
  if ((isObjectEmpty(saleData.serviceLabel) || isEmpty(saleData.serviceLabel?.label)) && channelDefVariant.autoGenerateLabel && isOffline()) {
    let serviceLabel = { label: getNewSaleToken() };
    newSaleData.serviceLabel = serviceLabel;
    store.dispatch(saveSaleData(newSaleData));
    // sale.set('serviceLabel', serviceLabel); 
  }

  // ----  if autoGenerateLabel is unchecked in sale chanel setting and audhoc label is empty  then shows error mssg  'Label is required for this channel.'

  if (channelDefVariant.labelRequired && (isObjectEmpty(newSaleData.serviceLabel) || isEmpty(newSaleData.serviceLabel?.label))) {
    store.dispatch(updateToastInfo({ toastMessage: getErrorMessage('UI_MSG_CHANNEL_REQUIRED', 'Label'), isToastOpen: true }));
    // this.onLabelsButtonTap();
    isValid = false;
  }

  /* ----------------------------------------------------------------------------------------------------------------------------------------------
  --- In sale channel setting -------------------------------------------------------------------------------------------------------------------
      customerinfo selected to required
      
      then
  ----  if customerinfo is empty then throw mssg  'Customer is required for this channel.'  and opens  customer tab panel 
     if ((channelDefVariant.customerOption === 'Prompt' && !saleData?.customerInfo && !sale.get('customerSkipped'))
                  || (channelDefVariant.customerOption === 'Required' && !customerPresent)) {
                    store.dispatch(updateToastInfo({toastMessage: getErrorMessage('UI_MSG_CHANNEL_REQUIRED', 'Customer'), isToastOpen: true}));
                  // this.onConsumerButtonTap();
                  return true;
              } */

  // ----------------------------------------------------------------------------------------------------------------------------------------------
  /* --- In sale channel setting -------------------------------------------------------------------------------------------------------------------
     in sale channel setting if delivery mode is checked and Pickup mode is checked
     
     then 
  ---  if deliverIfo is empty then throws mssg 'Delivery or Pickup info is required for this channel.'  and opens delivery ifo tab panel
  ---  if pickuIfo is empty then throws mssg 'Delivery or Pickup info is required for this channel.'  and opens pickup ifo tab panel
   */
  if (channelDefVariant.deliveryModes?.length && selectedMode !== SELECTION_MODES.DINE_IN) {
    if (isObjectEmpty(saleData.deliveryInfo)) {
      store.dispatch(updateToastInfo({ toastMessage: getErrorMessage('UI_MSG_CHANNEL_REQUIRED', 'Delivery or Pickup info'), isToastOpen: true }));
      /* this.onDeliveryLabelButtonTap();
      setTimeout(function () {
          me.onDeliveryLabelFormDoneButtonTap();
      }, 500); */
      isValid = false;
    } else if (channelDefVariant.deliveryModes.includes('Pickup') && !channelDefVariant.deliveryModes.includes('Delivery') && (!!saleData?.deliveryInfo?.mode && !["Pickup", "Pickup"].includes(saleData?.deliveryInfo?.mode))) {
      store.dispatch(updateToastInfo({ toastMessage: getErrorMessage('UI_MSG_CHANNEL_REQUIRED', 'Pickup info'), isToastOpen: true }));
      /* this.onDeliveryLabelButtonTap();
      setTimeout(function () {
          me.onDeliveryLabelFormDoneButtonTap();
      }, 500); */
      isValid = false;
    } else if (channelDefVariant.deliveryModes.includes('Delivery') && !channelDefVariant.deliveryModes.includes('Pickup') && (saleData?.deliveryInfo.mode !== 'Delivery')) {
      store.dispatch(updateToastInfo({ toastMessage: getErrorMessage('UI_MSG_CHANNEL_REQUIRED', 'Delivery info'), isToastOpen: true }));
      /* this.onDeliveryLabelButtonTap();
      setTimeout(function () {
          me.onDeliveryLabelFormDoneButtonTap();
      }, 500); */
      isValid = false;
    }
  }
  /* ----------------------------------------------------------------------------------------------------------------------------------------------
  --- In sale channel setting -------------------------------------------------------------------------------------------------------------------
     in sale channel setting if sourceOrderNumRequired is checked
     
     then 
  ---  if Source Order Number is empty then throws mssg 'Source Order Number is required.' and open delivery tab panel */
  // if (channelDefVariant.sourceOrderNumRequired && (isObjectEmpty(saleData.sourceInfo) || isEmpty(saleData?.sourceInfo?.invoiceNumber))) {
  //   store.dispatch(updateToastInfo({toastMessage: getErrorMessage('UI_VALIDATION_ERR_REQUIRED', 'Source Order Number'), isToastOpen: true}));
  //   // this.onDeliveryLabelButtonTap();
  //   // setTimeout(function () {
  //   //     me.onDeliveryLabelFormDoneButtonTap();
  //   // }, 500);
  //   return true;
  // }
  /* ----------------------------------------------------------------------------------------------------------------------------------------------
  --- -------------------------------------------------------------------------------------------------------------------
     isCustomerAuthenticationRequired  is required 
     
     then 
  ---  if customer info is empty then throws mssg 'Source Order Number is required.'
  ---- if customer info  is not empty and not authenticated then  opens onShowCustomerAuthentication popup  */
  if (checkForCustInfo && selectedChannelData?.channelDefId && channelDefCatalogData) {
    const matchedChannel = channelDefCatalogData.find(
      (channel: any) => channel?.mid === selectedChannelData?.channelDefId
    );
    let isCustomerRequired = false;
    if (matchedChannel) {
      if (checkIsEmpty(matchedChannel?.variants) && matchedChannel?.customerOption === REQUIRED) {
        isCustomerRequired = true;
      } else if (channelDefVariant?.customerOption === REQUIRED) {
        isCustomerRequired = true;
      }
    }

    if (isCustomerRequired && checkIsEmpty(saleData?.customerInfo)) {
      store.dispatch(updateToastInfo({ toastMessage: getErrorMessage('UI_ERR_CUST_AUTH_REQD'), isToastOpen: true }));
      isValid = false;
      return { updatedSaleData: newSaleData, isValid };
    }
  }

  if (data.isCustomerAuthenticationRequired(saleData) && businessData?.authenticateCustomer) {
    /* if (Rista.AppConstants.getOffline()) {
      store.dispatch(updateToastInfo({toastMessage: getErrorMessage('UI_ERR_CUST_AUTH_OFFLINE'), isToastOpen: true}));
        return true;
    } */
    if (!saleData?.customerInfo?.authenticated) {
      store.dispatch(updateToastInfo({ toastMessage: getErrorMessage('REST_CUSTOMER_NOT_AUTHENTICATED'), isToastOpen: true }));
      isValid = false;
    }
  }
  return { updatedSaleData: newSaleData, isValid };
}

export const isCustomerInfoMissing = (
  customerOption: string | undefined,
  customerInfo: any,
) => {
  if (customerOption !== REQUIRED) {
    return false;
  }

  if (!customerInfo || isObjectEmpty(customerInfo)) {
    return true;
  }

  return (
    !customerInfo?.customerId &&
    !customerInfo?.contactInfo?.phoneNumber
  );
};

export const warningPrinterModal = () => {
  const saleData = getClonedSaleDataFromReducer();
  var serviceLabel = saleData?.serviceLabel;
  if (serviceLabel && serviceLabel.resourceId && !saleData.printLog) {
    return true;
  }
  return false;
}

export const getNewSaleToken: any = () => {
  const businessData = store.getState().businessStore?.businessData;
  var saleToken = JSON.parse(getLocalStorage('saleToken') || '{}'), currentDate = (new Date()).getDate(),
    label = (businessData?.deviceInfo?.label || businessData?.deviceInfo?.serialNumber).toString();
  if (!saleToken.day || saleToken.day !== currentDate || saleToken.number >= 99) {
    saleToken = { day: currentDate, number: 1 };
  } else {
    saleToken.number++;
  }
  setLocalStorage('saleToken', JSON.stringify(saleToken));
  return [label.substring(label.length - 1), '0', saleToken.number].join('');
}

export const isObjectEmpty: any = (obj: any) => {
  for (var prop in obj) {
    console.log("checkk herre---------->", obj, prop);
    if (obj.hasOwnProperty(prop))
      return false;
  }
  return true;
}

export const isEmpty: any = (string: string) => {
  var isEmpty = true;
  if (string.trim()) {
    isEmpty = false;
  }
  return isEmpty;
}

// Moved to @rista/core/helpers/commonHelpers (a split — the two platforms clone differently).
export { deleteErrorsField } from "@rista/core/helpers/commonHelpers";

export const isValidEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

export const getLastKeyInMap = (map: any = new Map()) => {
  if (map.size > 0) {
    return [...map][map.size - 1][0];
  }
  return '';
};

export const getPreviousDate = (daysToSubtract: any = 1) => {
  return moment().subtract(daysToSubtract, "days");
}
/**
 * 
 * @param itemIndex 
 * @returns reversed index according to sale items
 */
export const getReversedIndex = (itemIndex: number) => {
  let saleData = store.getState().saleDataStore?.saleData;
  let itemsCount = saleData?.saleItems?.length || 0;
  return itemsCount - itemIndex - 1;
};

// Moved to @rista/core/utils/currency — THE currency module, which now owns the symbol table and
// the resolver together. It was a platform split until 2026-09-18 (mobile's `currency` indexed a
// one-key constants map, with no library lookup and no HTML decoding, and printed `undefined` for
// every other currency); this file's body is the one that survived, unchanged. The private
// `decoder`/`decodeHtmlEntity` pair went with it — nothing else here used them — and is now a pure
// hex-entity decoder, since core must run on a platform with no `document`. The currency table is
// IMPORTED from @dotpe/rista-library rather than read off `window.RL` (check K).
// Re-exported through core's `helpers/commonHelpers`, which keeps this import path working.
// Nothing in this file's body calls it, so a re-export is sufficient (ledger rule 5).
export { currency } from "@rista/core/helpers/commonHelpers";

// Moved to @rista/core/helpers/myOrderHelpers (2026-09-15, rider row). Re-export only.
export { countUnverifiedItems } from "@rista/core/helpers/myOrderHelpers";

export const isVerifiedQuantityAvailable = (orderInfo: any) => {
  if (orderInfo?.saleItems.length > 0 && orderInfo?.saleItems[0]?.verifiedQuantity) { return true; }
  else {
    return false;
  }
}

export { isVerifiedStatus } from "@rista/core/helpers/myOrderHelpers";

export const parseIp = (data: any = []) => {
  let ipV4: any = [];
  data.forEach(function (i: any) {
    if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(i.address)) {
      ipV4.push(i);
    }
  });
  return ipV4;
}
export function openIntent(intentURL: any) {
  // if (Rista.CommonHelper.isChrome() || Rista.CommonHelper.isWindowsApp()) {
  window.open(intentURL, '_system');
  setTimeout(() => {
    exec(NW_APP_EVENT_KEYS.windowClose, "", []);
  }, 15000);
}

// Moved to @rista/core/helpers/commonHelpers with the feature gate (identical in both apps).
// Nothing in this file's own body calls them, so a re-export is enough (ledger rule 5).
export { allowedFeatures, allowedModules, getAssignedLicence } from "@rista/core/helpers/commonHelpers";

/**
 * Moved to `@rista/core/helpers/commonHelpers` with the NAVIGATION seam. Re-exported so this
 * file's importers are unchanged; nothing in this file's own body calls it (ledger rule 5).
 */
export { redirectToLicenseLandingPage } from "@rista/core/helpers/commonHelpers";

export function isAssignedLicenceSEP() {
  return store.getState()?.businessStore?.userData?.assignedLicence === "SEP";
}

export function isCurrentOsMac() {
  return window.navigator.platform === "MacIntel";
}

export const resetNotificationvalue = () => {
  stopDesktopNotification();
  store.dispatch(updateNotificationValue(0));
  return;
}

export const validateIpv4 = (inputStr: string) => {
  let ipv4_regex = /^(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)(?:\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)){3}$/gm;
  return ipv4_regex.test(inputStr);
}

export function clearUserLoginData() {
  removeLocalStorage(LOCALSTORAGE_KEYS.isSupportLogin);
  removeLocalStorage(LOCALSTORAGE_KEYS.aToken);

  store.dispatch(setModuleSettingsLoaded(false));
  setOffline(false);
  store.dispatch(updateOfflineLogin({
    offlineLogin: false
  }));

  store.dispatch(userLogout());
}

export const clearSupportLoginData = () => {
  clearAppLsData();
  resetOfflineData();
  // Dispatch the supportLogout action - slices handle their own reset via extraReducers
  store.dispatch(supportLogout());
}

export const handleLogoutAction = () => {
  logEvent(LOG_LEVELS.INFO, 'handleLogoutAction CALLED', {
    timestamp: new Date().toISOString(),
  });

  // KIOSK MODE: a customer must never see the login screen. Every forced-logout path in
  // the app funnels through here, so this one branch covers the API interceptor, the
  // login saga and the offline helper alike. The kiosk parks on its own dead-end screen
  // and staff recover it through the hidden exit — see docs/KIOSK_ARCHITECTURE.md §13.10.
  //
  // ⚠️ KNOWN, OPEN: this reads the mode flag as it is WHEN THE RESPONSE LANDS, and nothing
  // ties the response to the session that issued the request — so a forced-logout response
  // still in flight from a dead session can park a kiosk that staff have since recovered
  // (exit → log in → re-enable). Narrow: it needs a response to outlive a human login. Any
  // fix belongs here AND at the mobile interceptor, and has to compare session identity, not
  // the flag. Written up under §13.10's "Still open: a late forced-logout response".
  if (isKioskModeEnabled()) {
    store.dispatch(markSessionExpired());
    return;
  }

  if (isSupportLogin()) {
    clearSupportLoginData();
    if (isAndroid()) {
      sendDataToApp({ eventName: REACT_NATIVE_APP_EVENTS.MERCHANT_LOGIN_ACTION });
    } else {
      history.push(LOGIN_ROUTE);
    }
  } else {
    clearUserLoginData();
    if (isAndroid()) {
      sendDataToApp({
        eventName: REACT_NATIVE_APP_EVENTS.LOGOUT,
      });
    } else {
      history.push(LOGIN_ROUTE);
    }
  }
};

export function generateArrayTillANumber(number: number) {
  if (typeof number !== 'number' || number < 0) {
    throw new Error('Input must be a non-negative number.');
  }

  return Array.from(Array(number).keys());
}

// `isPermittedBranchForUser` + `createBranchPermittedUsersMap` (its only caller) now live in
// @rista/core/helpers/commonHelpers. Re-exported below, next to where the map builder was.
/* this function provides the priority tag for an item which has multiple tags using the priority we have set in our store */
export function provideItemTag(tagsArr: any = []) {
  let priority = store.getState()?.catalogStore?.tagsPriorityArr || [];
  if (!priority?.length || !tagsArr?.length) {
    return null;
  }
  if (tagsArr?.includes(priority?.[0])) {
    return NVEG;
  }
  if (tagsArr?.includes(priority?.[1])) {
    return EGG;
  }
  if (tagsArr?.includes(priority?.[2])) {
    return VEG;
  }
  return null;
}

export const isSourcePosMobile = () =>
  getLocalStorage(APP_SOURCE) === APP_SOURCE_TYPES.POS_MOBILE;

export function executeWebviewHostFunction(actionName: string, args: any[]) {
  console.log("------executeWebviewHostFunction---", actionName);
  sendDataToApp({
    eventName: REACT_NATIVE_APP_EVENTS.webviewAction,
    actionName,
    args,
  });
}

export const isPineLabDevice = () => {
  return false;
  // return isAndroid();
};

export const concatString = (array: any[]) => {
  let result = "";
  for (var i = 0; i < array.length; ++i) {
    result += array[i];
  }
  return result;
};

/**
 * This function will convert array to object using key
 * @param {*} inputArr
 */

// Moved to @rista/core/helpers/commonHelpers (index.desktop.ts — WEB-ONLY; mobile's
// commonHelpers has no equivalent) — re-exported so this file's importers are unchanged.
export { convertArrayToObjectWithoutLowerCase } from "@rista/core/helpers/commonHelpers";

export function getCurrentVersionText() {
  const versions = getCurrentVersions();
  const versionText = `[${versions.oracleVersion}-${versions.nwAppVersion || "web"}]-[${versions.commonVersion}-${versions.ristaPosCommonVersion}]`;
  return versionText;
}

export function getCurrentVersionShortText() {
  const versions = getCurrentVersions();
  const versionText = `${versions.oracleVersion}-${versions.nwAppVersion || "web"}`;
  return versionText;
}

export type ViewTypes = keyof typeof viewTypes;

export function getNextViewType(type: ViewTypes): ViewTypes {
  const typesArray: ViewTypes[] = Object.keys(viewTypes) as ViewTypes[];
  const currentIndex = typesArray.indexOf(type);
  const nextIndex = (currentIndex + 1) % typesArray.length;
  return typesArray[nextIndex];
}
export function notificationTypes(title: string) {
  if (NOTIFICATIONS_EVENTTYPES[title]) return true;
  return false;
}
/**
 * 
 * @param item 
 * @returns punchedName if items name is overridden else longName
 */
export function getItemName(item: any) {
  return item?.punchedName || item?.longName;
}

/**
 * 
 * @param arr array of objects
 * @param key key to pick for uniqueness
 * @returns a new array with unique objects based on the given key
 */
// Moved to @rista/core/helpers/commonHelpers (shared.ts — pure, and mobile had no copy at all,
// so this is web's definition now shared). Re-exported so this file's importers are unchanged.
export { getUniqueListByKey } from "@rista/core/helpers/commonHelpers";

/**
 * Tries to parse a given string as JSON. If successful, returns the parsed value. 
 * If not, returns the original string.
 * @param {string} jsonString - The string to be parsed
 * @returns {any} The parsed value or the original string
 */
export function tryJsonParse(jsonString: string): any {
  try {
    let parsedValue = JSON.parse(jsonString);
    return parsedValue;
  }
  catch (e) { }
  return jsonString;
};

/**
 * 
 * @returns position for delivery modal backdrop position based on License Expiry Banner is visible or not
 */
export function getModalAndBackdropPosition(showBanner: boolean) {
  if (showBanner) return { backdrop: 178, modal: window.innerHeight - 117 };
  return { backdrop: 117, modal: window.innerHeight - 58 };
}

/**
 * Returns the current operating system.
 * @returns The current operating system. For modern browsers, this is determined by
 * the `navigator.userAgentData.platform` property. For older browsers, this is determined by
 * the `navigator.platform` property.
 */
export function getCurrentAppType(): string {
  const appVersions = getCurrentVersions();
  const nwAppVersion = appVersions.nwAppVersion;
  if (!nwAppVersion || typeof nwAppVersion !== "string") return "web";
  let curOS;
  if ((navigator as any).userAgentData) {
    curOS = (navigator as any).userAgentData.platform;
  } else {
    curOS = navigator.platform;
  }
  switch (curOS) {
    case "Win32":
    case "Windows":
      return "windows";
    case "MacIntel":
    case "macOS":
      return "osx";
    default:
      return curOS;
  }
}

/**
 * Determines the current platform based on the stored platform information.
 * @returns {string} - Returns "windows" if the platform is Windows, "osx" if the platform is Mac, 
 * "web" if the platform is not stored, or an empty string for other platforms.
 */
// Returns true or false. Used to decide the Cta text in cash register modal
export function isCloseBalanceType() {
  const cashRegisterModalType = store.getState().cashRegisterStore.cashRegisterModalType;
  return cashRegisterModalType === CASH_REGISTER_MODAL_TYPES.closing_balance ||
    cashRegisterModalType === CASH_REGISTER_MODAL_TYPES.close_switch_branch ||
    cashRegisterModalType === CASH_REGISTER_MODAL_TYPES.close_switch_user;
}
export function setrecentUser(userData: any) {
  let recentUsers: any[] = [];
  try {
    recentUsers = getRecentUsersFromStorage();
    recentUsers = recentUsers.filter((u: any) => u.userId !== userData.userId);
    recentUsers.unshift(userData);
    if (recentUsers.length > 4) {
      recentUsers.pop();
    }
  } catch (error) {
    console.error("Failed to parse recentUsers from localStorage:", error);
    recentUsers = [userData];
  } finally {
    setLocalStorage(LOCALSTORAGE_KEYS.RECENT_USER, JSON.stringify(recentUsers));
    setLocalStorage(LOCALSTORAGE_KEYS.USER_DATA, JSON.stringify(userData));
  }
}
export function getRecentUsersFromStorage() {
  try {
    const recentUsersRaw = getLocalStorage(LOCALSTORAGE_KEYS.RECENT_USER);
    const lastUserRaw = getLocalStorage(LOCALSTORAGE_KEYS.USER_DATA);
    let lastUser: any;
    if (lastUserRaw) {
      lastUser = JSON.parse(lastUserRaw as string);
    }
    if (!recentUsersRaw) return [];
    let parsedData = JSON.parse(recentUsersRaw as string);
    if (lastUser) {
      parsedData = parsedData?.filter((user: any) => user?.userId !== lastUser?.userId);
      parsedData = [lastUser, ...parsedData];
    }
    return Array.isArray(parsedData) ? parsedData : [];
  } catch (error) {
    console.error("Error parsing recentUsers from localStorage:", error);
    return [];
  }
};
export const fetchPosSettings = () => {
  let businessData = store.getState().businessData;
  const callback = () => {
  };
  businessData.branchId && store.dispatch(getPOSSettingsData({ branchId: businessData.branchId, callback }));
};

export function isCatalogOverThreshold() {
  return getLocalStorage("IS_CATALOG_OVER_THRESHOLD") === "true";
  // const allItemsList = store.getState().itemsStore?.allItemsList || [];
  // const ITEMS_COUNT_THRESHOLD = 1000;
  // const itemsCount = allItemsList?.length || 0;
  // if (itemsCount > ITEMS_COUNT_THRESHOLD) {
  //   return true;
  // }
  // return false;
}

// Both now live in @rista/core — one definition, no drift. Re-export only: nothing else in
// this file calls either.
export {
  isPermittedBranchForUser,
  createBranchPermittedUsersMap,
} from "@rista/core/helpers/commonHelpers";

export const shouldEnableClarity = () => {
  const accountUUID = store.getState().businessStore?.businessData?.accountUUID;
  if (!accountUUID) return false;
  return CLARITY_ACCOUNT_UUIDS.includes(accountUUID);
}

export function offlineSoftLock(leftAttemptCount: number, setLeftAttemptCount: any, triggerSoftLock: any) {
  if (leftAttemptCount === LOGIN_ATTEMPT_INITIAL_COUNT) {
    setLeftAttemptCount(3);
  }
  if (leftAttemptCount > 1) {
    setLeftAttemptCount((prev: any) => prev - 1);
  }
  else if (leftAttemptCount === 1) {
    triggerSoftLock();
  }
}

/**
 * Pure validation function.
 * Takes a credential value, its mode, and an optional custom rules list.
 * Returns `{ valid: true }` or `{ valid: false, error: string }` with the
 * label of the first failing rule as the error message.
 */
export interface ValidationRule {
  key: string;
  label: string;
  test: (value: string) => boolean;
}

export const validateUserCredentials = (
  value: string,
  validationRules: ValidationRule[],
): { valid: true } | { valid: false; error: string } => {
  const rules = validationRules;
  for (const rule of rules) {
    if (!rule.test(value)) {
      return { valid: false, error: rule.label };
    }
  }
  return { valid: true };
};
// Moved to @rista/core/helpers/commonHelpers. Web-only. Re-export only — this file's own body
// does not call it (ledger rule 5).
export { triggerLogout } from "@rista/core/helpers/commonHelpers";

// Moved to @rista/core/helpers/commonHelpers (index.desktop.ts — web-only). Re-export only:
// nothing in this file's body calls it.
export { isQuickCilverPaymentAmountValidated } from "@rista/core/helpers/commonHelpers";

/**
 * Filters loyalty providers using the RL library's `filterForProviders` algorithm.
 *
 * @param loyaltyProviders - Full list of configured loyalty providers
 * @param params - Filter params; should include `branchId` and `channelDefId`
 * @returns Filtered array of matching providers; empty array on library error
 */
// Moved to @rista/core/helpers/commonHelpers (index.desktop.ts — web-only so far).
export { filterLoyaltyProviders } from "@rista/core/helpers/commonHelpers";

export function parseItemLanguage(item: any, itemType: ItemType = ITEM_TYPE.ITEM) {
  const secondaryLangCode = store.getState().businessStore?.businessData?.branchadditionalLanguages[0]?.languageCode;
  if (!secondaryLangCode) return item;

  const { keys, useInfoPath } = ITEM_TYPE_CONFIG[itemType];
  const i18nSource = useInfoPath ? item.info?.i18n : item.i18n;
  const translations = i18nSource?.[secondaryLangCode];

  if (!translations) return item;

  const parsedItem = { ...item };
  if (useInfoPath) {
    parsedItem.info = { ...parsedItem.info };
    keys.forEach(key => {
      if (translations[key] != null) parsedItem.info[key] = translations[key];
    });
  } else {
    keys.forEach(key => {
      if (translations[key] != null) parsedItem[key] = translations[key];
    });
  }

  return parsedItem;
}

// Moved to @rista/core/helpers/commonHelpers — re-exported so this file's importers are
// unchanged. NOTE mobile imported the same function from helper/catalogueHelpers; core holds the
// single definition and both apps re-export it.
export { convertArrayToObject } from "@rista/core/helpers/commonHelpers";

// Moved to @rista/core/helpers/commonHelpers. The whole cluster went together, so no second
// definition was left here. `shouldCalculateSale` and `isSubwayAccount` are in its shared.ts since
// 2026-09-29 (My Orders v2 recalculates on both platforms); `preventCloseSale` stays in
// index.desktop.ts — web-only, mobile has no preventCloseSale.
export { shouldCalculateSale, preventCloseSale } from "@rista/core/helpers/commonHelpers";

// Moved to @rista/core/helpers/commonHelpers (index.desktop.ts). All three went together:
// `removeOrderFromArrayBasedOnKey` is the only dependency of the other two, and leaving it here
// would have put a second definition beside core's. Web-only — none of the three exists in
// apps/mobile.
export {
  clearAcceptedOrder,
  clearAcceptedOrderFromNotifications,
  removeOrderFromArrayBasedOnKey,
} from "@rista/core/helpers/commonHelpers";

// Moved to @rista/core/helpers/commonHelpers.
//   shared.ts        -> getBranchId (byte-identical in both apps)
//   index.desktop.ts -> getCurrentVersions, getCurrentPlatform, getLoginMetaInfo. The first two
//                       are web-only; getLoginMetaInfo exists on both but the two payloads share
//                       only `loginType`, so each platform keeps its own body.
// The build-time git values getCurrentVersions used to read from ./generatedGitInfo.json now
// arrive through the effects bridge's env bag — see bootstrapCoreBridges.ts.
// 🔴 getCurrentVersions is IMPORTED as well as re-exported: this file's own body still calls it
// in getCurrentVersionShortText and two other places, and an `export ... from` re-export creates
// NO local binding (ledger rule 5 / hazard 30). tsc caught this as 3 x TS2304 — in a `.js` file
// the same mistake would have shipped as a runtime ReferenceError.
import { getCurrentVersions } from "@rista/core/helpers/commonHelpers";
export { getCurrentVersions };
export {
  getBranchId,
  getCurrentPlatform,
  getLoginMetaInfo,
} from "@rista/core/helpers/commonHelpers";
